// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewWorkspace.test.tsx
//
// The review screen's right column: the artwork header with its pane tabs and
// the viewers, with the pins drawn the way the pin view and a comparison ask.
// The workspace owns the state; this column only lays it out.
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { AskV2ReviewArtworkHeader } from "./AskV2ReviewArtworkHeader";
import { AskV2ReviewCompareLayout } from "./AskV2ReviewCompareLayout";
import {
  AskV2ReviewDraftPinsNote,
  draftPinTone,
} from "./AskV2ReviewDraftPinsNote";
import { AskV2ReviewPaneTabs } from "./AskV2ReviewPaneTabs";
import { AskV2ReviewPinGroupBar } from "./AskV2ReviewPinGroupBar";
import { severityPinTone } from "./pinGroups";
import { type useAskPunttReview } from "./useAskPunttReview";
import { type AskV2ReviewGroupPins } from "./useAskV2ReviewGroupPins";
import { type AskV2ReviewPinView } from "./useAskV2ReviewPinView";
import { type useAskV2ReviewVersionCompare } from "./useAskV2ReviewVersionCompare";
import { type useAskV2ReviewWorkspace } from "./useAskV2ReviewWorkspace";
import { comparePinTone } from "./versionChanges";
import { type ReviewVersionOption } from "./VersionPicker";

import {
  type HumanCommentSubmitInput,
  type useReviewArtworkFocus,
} from "../../components/ask-puntt-review";
import { type mapReviewFilesToAssets } from "../../components/ask-puntt-review/mapReviewAssets";
import { type AskV2TransportApi } from "../transport/useAskV2Transport";

type Review = NonNullable<
  NonNullable<ReturnType<typeof useAskPunttReview>["data"]>["review"]
>;
type ArtworkFocus = ReturnType<typeof useReviewArtworkFocus>;

type AskV2ReviewArtworkColumnProps = {
  review: Review;
  workspace: ReturnType<typeof useAskV2ReviewWorkspace>;
  compare: ReturnType<typeof useAskV2ReviewVersionCompare>;
  pinView: AskV2ReviewPinView;
  groupPins: AskV2ReviewGroupPins;
  versionOptions: readonly ReviewVersionOption[];
  historyMessages: AskV2TransportApi["historyMessages"];
  artworkFiles: ReturnType<typeof mapReviewFilesToAssets>;
  artworkPaneRef: ArtworkFocus["artworkPaneRef"];
  artworkHeaderControls: ReactNode;
  setArtworkHeaderControls: ArtworkFocus["setArtworkHeaderControls"];
  selectedCommentId: string | null;
  selectComment: (commentId: string) => void;
  clearComment: () => void;
  commentNumbers: Readonly<Record<string, number>>;
  /** The Comments tab's badge: every finding the list draws and the pins show. */
  commentCount: number;
  currentUserId?: string;
  /** Absent, the artwork offers no human-comment composer (read-only viewer). */
  onSubmitHumanComment?: (input: HumanCommentSubmitInput) => Promise<void>;
};

export function AskV2ReviewArtworkColumn({
  review,
  workspace,
  compare,
  pinView,
  groupPins,
  versionOptions,
  historyMessages,
  artworkFiles,
  artworkPaneRef,
  artworkHeaderControls,
  setArtworkHeaderControls,
  selectedCommentId,
  selectComment,
  clearComment,
  commentNumbers,
  commentCount,
  currentUserId,
  onSubmitHumanComment,
}: AskV2ReviewArtworkColumnProps) {
  const { t } = useTranslation("ui");
  const { isComparing } = compare;
  const { groupedPins, barGroup, toggleGroup } = groupPins;
  const isReviewing = review.aiRunning === true;

  const headerControls = (
    <div
      className="ml-auto flex items-center gap-2"
      data-testid="ask-v2-review-header-controls"
    >
      {artworkHeaderControls}
    </div>
  );

  const paneTabs = (
    <AskV2ReviewPaneTabs
      data-testid="ask-v2-review-pane-tabs"
      leftPane={workspace.leftPane}
      // why: the badge counts every finding the list draws and the pins
      // show. Counting `review.comments` instead let the tab read
      // "Comments (3)" beside a list of 2 after a reject.
      commentCount={commentCount}
      onChange={workspace.setLeftPane}
    />
  );

  return (
    <div
      className="flex min-h-0 min-w-0 flex-[3] flex-col overflow-hidden bg-puntt-neutral-gray-2"
      data-testid="ask-v2-review-artwork"
      // why: same prohibited-attribute drop as the workspace wrapper --
      // "Artwork" is the only thing telling this pane apart from the
      // findings column, and a bare `<div>` may not carry a name.
      role="region"
      aria-label={t("askV2.review.artworkLabel")}
    >
      <AskV2ReviewArtworkHeader
        review={review}
        compare={compare}
        versionOptions={versionOptions}
        onSwitchReview={workspace.switchReview}
        historyMessages={historyMessages}
        paneTabs={paneTabs}
        viewerControls={headerControls}
      />
      <AskV2ReviewCompareLayout
        review={review}
        compare={compare}
        artworkFiles={artworkFiles}
        artworkPaneRef={artworkPaneRef}
        setArtworkHeaderControls={setArtworkHeaderControls}
        selectedCommentId={selectedCommentId}
        commentNumbers={commentNumbers}
        currentUserId={currentUserId}
        comparedPinView={groupPins.comparedPinView}
        openPins={{
          comments: groupedPins
            ? pinView.viewerComments
            : compare.viewerComments,
          numbers: groupedPins
            ? groupPins.groupPinNumbers
            : compare.viewerNumbers,
          activeId: pinView.pinOf(compare.steppedPin?._id ?? selectedCommentId),
          tone: isComparing
            ? comparePinTone(compare.openSide)
            : isReviewing
              ? draftPinTone
              : severityPinTone,
          cluster: groupPins.groupPinCluster,
        }}
        overlay={
          <>
            {barGroup ? (
              <AskV2ReviewPinGroupBar
                group={barGroup}
                onBack={() => toggleGroup(barGroup.key)}
                onStep={(direction) => {
                  workspace.setLeftPane("comments");
                  selectComment(
                    pinView.stepFinding(direction, selectedCommentId, barGroup),
                  );
                }}
              />
            ) : null}
            {isComparing ? null : (
              <AskV2ReviewDraftPinsNote
                running={isReviewing}
                count={review.comments.length}
              />
            )}
          </>
        }
        onOpenComment={(commentId) => {
          // A closed group's pin opens the group, not its first finding.
          const hub =
            groupedPins && !pinView.openGroup
              ? pinView.hubGroup(commentId)
              : null;
          workspace.setLeftPane("comments");
          if (hub && hub.members.length > 1) {
            pinView.setOpenGroupKey(hub.key);
            return;
          }
          selectComment(commentId);
        }}
        onArtworkBackgroundClick={() => {
          // Stryker disable next-line ConditionalExpression: here clearComment only sets the local selection to null (no deep-link callback is passed), so clearing an empty selection changes nothing; forcing it false is killed by "clears the selected finding and its chip when the artwork background is clicked"
          if (selectedCommentId != null) {
            clearComment();
          } else if (barGroup) {
            pinView.setOpenGroupKey(null);
          }
        }}
        onSubmitHumanComment={onSubmitHumanComment}
      />
    </div>
  );
}
